import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user !== false) {
    const from = location.state?.from || "/admin";
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(location.state?.from || "/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="admin-login-page" className="min-h-[80vh] flex items-center justify-center bg-faytex-pinkLight/40 px-4 py-16">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-hover border border-gray-100 p-8 md:p-10">
        <div className="w-12 h-12 rounded-full bg-faytex-pink text-white flex items-center justify-center mx-auto">
          <Lock className="w-5 h-5" />
        </div>
        <h1 className="font-heading text-3xl md:text-4xl mt-5 text-center tracking-tight">Admin sign in</h1>
        <p className="text-center text-sm text-faytex-muted mt-2">
          Access the Faytex catalog admin.
        </p>

        <form onSubmit={onSubmit} data-testid="admin-login-form" className="mt-8 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">Email</label>
            <input
              data-testid="admin-login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">Password</label>
            <input
              data-testid="admin-login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
              required
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-7 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-[11px] text-faytex-muted text-center">
          Protected area. Authorized personnel only.
        </p>
      </div>
    </div>
  );
}
